import { createEffect, createEvent, createStore, sample } from "effector";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { IRouterContract } from "@features/shared/router";
import {
    trackAiAssistantBackToLearningClick,
    trackAiAssistantHintGotItClick,
    trackAiAssistantStayInChatClick
} from "@features/shared/events";
import { AnalyticsService } from "@features/shared/analytics";

export const backToLearningClicked = createEvent<void>();
export const stayInChatClicked = createEvent<void>();
export const hideInChatBubble = createEvent<void>();

export const messageReportOpened = createEvent<Record<string, any>>();
export const messageReportHidden = createEvent<void>();
export const selectMessageReportType = createEvent<string>();
export const messageReportMessageChanged = createEvent<string>();
export const messageReportSent = createEvent<void>();

export const $messageReport = createStore<Record<string, any> | null>(null)
    .on(messageReportOpened, (_, payload) => payload)
    .reset(messageReportHidden);

export const $messageReportTypes = createStore<string[]>([])
    .on(selectMessageReportType, (list, t) => (list.includes(t) ? list.filter((x) => x !== t) : [...list, t]))
    .reset(messageReportHidden);

export const $messageReportMessage = createStore<string>("")
    .on(messageReportMessageChanged, (_, v) => v)
    .reset(messageReportHidden);

export const $messageReportSent = createStore<boolean>(false)
    .on(messageReportSent, () => true)
    .reset(messageReportHidden);

const contract = new ModelContract(
    {
        $messageReport,
        $messageReportTypes,
        $messageReportMessage,
        $messageReportSent
    },
    {
        backToLearningClicked,
        stayInChatClicked,
        hideInChatBubble,
        messageReportOpened,
        messageReportHidden,
        selectMessageReportType,
        messageReportMessageChanged,
        messageReportSent
    },
    {},
    {}
);

export type IAssistantModelContract = typeof contract;

export class AssistantModelModule extends ModuleDescription<IAssistantModelContract> {
    constructor(public routerModule: IRouterContract) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        const trackBackToLearningFx = createEffect(() => trackAiAssistantBackToLearningClick());
        const trackStayInChatFx = createEffect(() => trackAiAssistantStayInChatClick());
        const trackHintGotItFx = createEffect(() => trackAiAssistantHintGotItClick());
        const browserBackFx = createEffect(() => {
            if (typeof window !== "undefined") window.history.back();
        });
        const trackMessageReportFx = createEffect<Record<string, any>, void>((p) => {
            AnalyticsService.trackEvent("ai_assistant_message_report", p);
        });

        sample({
            clock: backToLearningClicked,
            target: browserBackFx
        });

        sample({
            clock: backToLearningClicked,
            target: trackBackToLearningFx
        });

        sample({
            clock: stayInChatClicked,
            target: trackStayInChatFx
        });

        sample({
            clock: hideInChatBubble,
            target: trackHintGotItFx
        });

        sample({
            source: {
                payload: $messageReport,
                issues: $messageReportTypes,
                issuesDescription: $messageReportMessage
            },
            clock: messageReportSent,
            filter: (s) => s.payload !== null && s.issues.length > 0,
            fn: (s) => ({
                ...s.payload,
                issues: s.issues,
                issuesDescription: s.issuesDescription
            }),
            target: trackMessageReportFx
        });
    }
}
